import Border, { IBorderValueProps } from '../../../puck/reusable-props/Border';
import { MarginType } from '../../../puck/reusable-props/margin';
import { PaddingType } from '../../../puck/reusable-props/padding';
import { FieldLabel } from '@measured/puck';
import BackgroungColorComponent, {
  IBackgroundProps,
} from '../../customComponents/BackgroungColorComponent';
import WithAccordion from '../../customComponents/CustomAccordion';
import PaddingComponent from '../../customComponents/PaddingComponent';
import MarginComponent from '../../customComponents/MarginComponent';
import InputField from '../../../../components/common/input-field';
import { getNumberPatternWithAuto } from '../../../utils/helper';
import CustomTooltip from '../../../../components/common/tooltip';
import { Select } from '../../form-fields/Select';
import {
  deviceOptions,
  GapOptions,
  JustifyOptions,
} from '../../../utils/constants';
import ShowComponent from '../../showComponents';
import {
  boxShadows,
  flexDirections,
  verticalAligns,
} from '../../../puck/reusable-props/options';
import WidthComponent, {
  IWidthComponentProps,
} from '../../blocks/widthComponent';
import Toggle from '../../../../components/common/toggleButton';

export interface IJustifyContent {
  currentDevice: 'mobile' | 'tablet' | 'desktop';
  justify: {
    mobile: string;
    tablet: string;
    desktop: string;
  };
}

export interface BodyProps {
  background: IBackgroundProps;
  border: IBorderValueProps;
  margin: MarginType;
  padding: PaddingType;
  width: IWidthComponentProps;
  maxWidth: IWidthComponentProps;
  showComponent?: string[];
  justifyContent: IJustifyContent;
  customize: {
    height: string;
    gap: string;
    flexDirection: string;
    boxShadow: string;
    verticalAlign: string;
  };
}

interface IProps {
  value: BodyProps;
  onChange: (_value: BodyProps) => void;
}

export const Body = ({ value, onChange }: IProps) => {
  return (
    <WithAccordion title="Container">
      <ShowComponent
        value={value?.showComponent}
        onChange={e => {
          onChange({ ...value, showComponent: e });
        }}
      />
      <PaddingComponent
        value={value.padding}
        onChange={padding => onChange({ ...value, padding })}
        title="Padding"
      />
      <MarginComponent
        value={value.margin}
        onChange={margin => onChange({ ...value, margin })}
        title="Margin"
      />
      <WidthComponent
        onChange={width => onChange({ ...value, width })}
        value={value.width}
      />
      <WidthComponent
        onChange={maxWidth => onChange({ ...value, maxWidth })}
        value={value.maxWidth}
        title="Max Width"
      />
      <Border
        value={value.border}
        onChange={border => {
          onChange({ ...value, border });
        }}
        title="Border"
      />
      <BackgroungColorComponent
        value={value.background}
        onChange={background => onChange({ ...value, background })}
      />
      <WithAccordion title="Justify Content">
        <Toggle<{ label: string; value: string; deviceType: string }>
          value={
            deviceOptions.find(
              item => item.value === value?.justifyContent?.currentDevice,
            ) || deviceOptions[0]
          }
          onChange={newValue => {
            onChange({
              ...value,
              justifyContent: {
                ...value?.justifyContent,
                currentDevice:
                  newValue?.deviceType as typeof value.justifyContent.currentDevice,
              },
            });
          }}
          heading={{ label: 'Device', value: 'device' }}
          options={deviceOptions}
          propName="deviceType"
        />
        <FieldLabel
          label={
            (
              <CustomTooltip title="Justify Content">
                <span>Justify Content</span>
              </CustomTooltip>
            ) as React.ReactNode as string
          }>
          <Select
            className="pagebuilder-theme"
            value={
              value?.justifyContent?.justify?.[
                value?.justifyContent?.currentDevice
              ] || ''
            }
            onChange={e =>
              onChange({
                ...value,
                justifyContent: {
                  ...value?.justifyContent,
                  justify: {
                    ...value.justifyContent.justify,
                    [value?.justifyContent?.currentDevice]: e.target.value,
                  },
                },
              })
            }
            options={JustifyOptions}
          />
        </FieldLabel>
      </WithAccordion>
      <WithAccordion title="Customize">
        <FieldLabel
          label={
            (
              <CustomTooltip title="Height">
                <span>Height</span>
              </CustomTooltip>
            ) as React.ReactNode as string
          }>
          <InputField
            value={value?.customize?.height}
            onChange={e => {
              const newValue = getNumberPatternWithAuto(e);
              if (newValue !== null) {
                onChange({
                  ...value,
                  customize: { ...value.customize, height: newValue },
                });
              }
            }}
          />
        </FieldLabel>
        <FieldLabel
          label={
            (
              <CustomTooltip title="Gap">
                <span>Gap</span>
              </CustomTooltip>
            ) as React.ReactNode as string
          }>
          <Select
            value={value?.customize?.gap}
            className="pagebuilder-theme"
            options={GapOptions}
            onChange={e => {
              onChange({
                ...value,
                customize: { ...value.customize, gap: e.target.value },
              });
            }}
          />
        </FieldLabel>
        <FieldLabel
          label={
            (
              <CustomTooltip title="Flex Direction">
                <span>Flex Direction</span>
              </CustomTooltip>
            ) as React.ReactNode as string
          }>
          <Select
            value={value?.customize?.flexDirection}
            className="pagebuilder-theme"
            options={flexDirections}
            onChange={e => {
              onChange({
                ...value,
                customize: {
                  ...value.customize,
                  flexDirection: e.target.value,
                },
              });
            }}
          />
        </FieldLabel>
        <FieldLabel
          label={
            (
              <CustomTooltip title="Shadow">
                <span>Box Shadow</span>
              </CustomTooltip>
            ) as React.ReactNode as string
          }>
          <Select
            value={value?.customize?.boxShadow}
            className="pagebuilder-theme"
            options={boxShadows}
            onChange={e => {
              onChange({
                ...value,
                customize: { ...value.customize, boxShadow: e.target.value },
              });
            }}
          />
        </FieldLabel>
        <FieldLabel
          label={
            (
              <CustomTooltip title="Vertical Align">
                <span>Vertical Align</span>
              </CustomTooltip>
            ) as React.ReactNode as string
          }>
          <Select
            value={value?.customize?.verticalAlign}
            className="pagebuilder-theme"
            options={verticalAligns}
            onChange={e => {
              onChange({
                ...value,
                customize: {
                  ...value.customize,
                  verticalAlign: e.target.value,
                },
              });
            }}
          />
        </FieldLabel>
      </WithAccordion>
    </WithAccordion>
  );
};
